import _ from 'lodash';
import jQuery from 'jquery';

const { mixinEvents } = Mobi2Go.app;
Mobi2Go._class = Mobi2Go._class || {};

const MapsService = (Mobi2Go._class.MapsService = function(location) {
    if (!(this instanceof Mobi2Go._class.MapsService)) {
        return new Mobi2Go._class.MapsService(location);
    }

    this.location = null;

    this.init(location);
});

_.extend(MapsService.prototype, {
    /**-------------------------------------------------------------------------
     */
    init(location) {
        this.location = location;
        this.coordinates = null;
        mixinEvents('MapsService', this);
    },

    /**-------------------------------------------------------------------------
     */
    requestCoordinates() {
        const platform = Mobi2Go.app.getPlatform();
        if (platform === 'ios' || platform === 'android') {
            return; // Rely on native app contain providing coordinates
        }
        const that = this;
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(function(position) {
                that.coordinates = position.coords;
                that.trigger('coordinatesAvailable', position.coords);
            });
        }
    },

    /**-------------------------------------------------------------------------
     * Manually set coordinates
     * This is used by the native container apps to pass in coordinates it receives
     * via native API. This is to avoid using the browser's
     * geolocation service - which will trigger an unappealing alert asking
     * the customer's permission for their location.
     */
    setCoordinates(coordinates) {
        this.coordinates = coordinates;
        this.trigger('coordinatesAvailable', coordinates);
    },

    /**-------------------------------------------------------------------------
     */
    getCoordinates() {
        return this.coordinates;
    },

    /**-------------------------------------------------------------------------
     */
    getPlacePredictions(input, callback) {
        if (!input || !Mobi2Go.app.maps_ready) {
            return _.defer(function() {
                callback([]);
            });
        }

        function wrappedCallback(list) {
            if (_.isFunction(callback)) {
                const filtered_list = filterPlacePredictions(list);
                callback(filtered_list);
            }
        }

        return getAutocompleteService().getPlacePredictions(
            this.createAutocompleteRequest(input),
            wrappedCallback
        );
    },

    /**-------------------------------------------------------------------------
     */
    getPlaceDetails(prediction, callback) {
        const that = this;
        if (!_.isFunction(callback)) {
            callback = function() {};
        }
        this.getPlaceDetails.cache = this.getPlaceDetails.cache || {};
        if (this.getPlaceDetails.cache[prediction.place_id] !== undefined) {
            return _.defer(function() {
                callback(that.getPlaceDetails.cache[prediction.place_id]);
            });
        }

        prediction.fields = [
            'address_component',
            'formatted_address',
            'geometry',
            'name',
            'place_id',
            'type',
        ];

        prediction.sessionToken = getAutocompleteSessionToken();
        // Session token is valid for a single place detail request - so we must
        // reset the token here
        resetAutocompleteSessionToken();

        return getPlacesService().getDetails(prediction, function(
            details,
            status
        ) {
            if (!details) {
                return;
            }
            that.getPlaceDetails.cache[prediction.place_id] = details;
            callback(details, status);
        });
    },

    /**-------------------------------------------------------------------------
     */
    createAutocompleteRequest(input) {
        const country = Mobi2Go.headoffice.country_iso3166_alpha2;

        const request = {
            input,
            types: ['geocode', 'establishment'],
            sessionToken: getAutocompleteSessionToken(),
        };

        if (country) {
            request.componentRestrictions = { country };
        }

        if (this.location) {
            request.location = new google.maps.LatLng(
                this.location.address.latitude,
                this.location.address.longitude
            );
            request.radius = 1000 * 10;
        }

        return request;
    },

    /**-------------------------------------------------------------------------
     */
    isPointInPolygon(point, vs) {
        // ray-casting algorithm based on
        // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
        point = new Point(point);
        const x = point[0];
        const y = point[1];

        let inside = false;
        for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            const xi = vs[i][0];
            const yi = vs[i][1];
            const xj = vs[j][0];
            const yj = vs[j][1];

            const intersect =
                yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
            if (intersect) inside = !inside;
        }

        return inside;
    },

    /**-------------------------------------------------------------------------
     * Returns distance in kilometers
     *
     * Haversine algorithm
     */
    getDistanceBetweenPoints(point1, point2) {
        point1 = new Point(point1);
        point2 = new Point(point2);

        function toRad(value) {
            return (value * Math.PI) / 180;
        }

        const latA = toRad(point1.getLatitude());
        const lngA = toRad(point1.getLongitude());
        const latB = toRad(point2.getLatitude());
        const lngB = toRad(point2.getLongitude());

        const dLat = latB - latA;
        const dLon = lngB - lngA;

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(latA) *
                Math.cos(latB) *
                Math.sin(dLon / 2) *
                Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return c * 6378.136;
    },
});

/**-----------------------------------------------------------------------------
 * Private Methods
 */
function getAutocompleteService() {
    if (getAutocompleteService.service) {
        return getAutocompleteService.service;
    }

    return (getAutocompleteService.service = new google.maps.places.AutocompleteService());
}

function getPlacesService() {
    if (getPlacesService.service) {
        return getPlacesService.service;
    }

    return (getPlacesService.service = new google.maps.places.PlacesService(
        jQuery('<div/>')[0]
    ));
}

/**
 * Session for Google billing purposes
 * Google will not charge for autocomplete requests if they are followed by a
 * place details request. Session tokens are used to batch Autocomplete and
 * Place detail requests.
 */
function getAutocompleteSessionToken() {
    if (getAutocompleteSessionToken.token) {
        return getAutocompleteSessionToken.token;
    }
    return (getAutocompleteSessionToken.token = new google.maps.places.AutocompleteSessionToken());
}

function resetAutocompleteSessionToken() {
    getAutocompleteSessionToken.token = null;
}

function filterPlacePredictions(list) {
    // Google's Places API returns places such as countries, towns etc.
    // We are only interested in places that are deliverable to, i.e.,
    // places with specific street addresses.
    // It's not possible to ask the google API to only return such results,
    // so we must attempt to manually filter results.

    const disallowed_types = ['country'];

    var list = _.filter(list, function(item) {
        return !_.intersection(item.types, disallowed_types).length;
    });

    // Google's prediction API returns the deprecated 'reference' property
    // which is an alternative way to identify a place. This 'reference'
    // sometimes returns the wrong address, so we must strip it from the
    // predictions list.
    return _.transform(
        list,
        function(result, item) {
            delete item.reference;
            if (item.place_id) {
                item.placeId = item.place_id;
            }
            result.push(item);
        },
        []
    );
}

/**
 * Workaround to enable emergency hotfix for conflicting google libraries
 */
window.__m2gClearMapsCache = () => {
    delete getAutocompleteService.service;
    delete getPlacesService.service;
    delete getAutocompleteSessionToken.token;
};

/**-----------------------------------------------------------------------------
 * Point helper
 */
function Point(data) {
    if (data.longitude !== undefined) {
        this.longitude = this[0] = data.longitude;
    }

    if (data[0] !== undefined) {
        this.longitude = this[0] = data[0];
    }

    if (data.latitude !== undefined) {
        this.latitude = this[1] = data.latitude;
    }

    if (data[1] !== undefined) {
        this.latitude = this[1] = data[1];
    }
}

Point.prototype.getLatitude = function() {
    return this.latitude;
};

Point.prototype.getLongitude = function() {
    return this.longitude;
};
